'use client';
import PageTitle from '@/entities/PageTitle/ui/PageTitle';

import { IPopularSection, IProduct } from '@/shared/api/getMainPage/types';

import { Container } from '@/shared/ui/Container';

import { formatCurrency } from '@/shared/utils/formatters';
import { twMergeExt } from '@/shared/utils/twMergeExt';
import { wordEnding } from '@/shared/utils/wordEnding';
import PageHeader from '@/widgets/PageHeader/ui/PageHeader';
import { ProductPreviewCard } from '@/widgets/product-preview-card';

import { cva } from 'class-variance-authority';
import React from 'react';

import { FC, useRef } from 'react';

interface Props {
  className?: string;
  popularSections: IPopularSection[];
  query?: string;
  recommendProducts: IProduct[] | [];
}

export const CatalogCategorySectionEmpty: FC<Props> = ({ className, query, recommendProducts }) => {
  const mainRef = useRef<HTMLDivElement>(null);

  return (
    <div className={twMergeExt(cvaRoot(), className)}>
      <Container className={'flex items-start gap-0.8'} variant={'small'}>
        <h1 className={'font-secondary text-h1 text-cBlack'}>Результаты поиска</h1>

        <div className={cvaTotalCount()}>{formatCurrency(0, true)} товаров</div>
      </Container>
      <Container className="flex flex-col sm:flex-row mt-4.8 mb-4 px-8.8 py-8.8 justify-between items-start border border-cBlack16 rounded-[3.2rem]">
        <h3 className="font-secondary font-medium max-w-[35rem] text-[3.2rem]">Ничего не нашли по запросу</h3>
        <p className="text-bodyL text-cBlack80">
          К сожалению, по вашему запросу `&quot;
          {query}&quot; ничего не найдено
        </p>
      </Container>
      {recommendProducts?.length ? (
        <>
          <PageHeader pt={'none'} pb={'large'}>
            <PageTitle
              text={'Рекомендуем'}
              small={`${recommendProducts.length} ${wordEnding(recommendProducts.length, ['товар', 'товара', 'товаров'])}`}
            />
          </PageHeader>
          <Container variant={'large'}>
            <div className={cvaProductsList()}>
              {recommendProducts.map((i: IProduct, index: number) => (
                <React.Fragment key={i.id}>
                  <ProductPreviewCard
                    {...i}
                    href={`${i.code}`}
                    isBgGray
                    key={i.id}
                    className={'bg-cLightGrey'}
                    variant={[0, 4, 6, 7].includes(index) ? 'large' : 'default'}
                  />
                </React.Fragment>
              ))}
            </div>
          </Container>
        </>
      ) : null}

      {/* <CatalogCategoriesSlider
        className={'mt-4.8'}
        items={popularSections}
      /> */}
      <div ref={mainRef}></div>
    </div>
  );
};

const cvaRoot = cva(['CatalogCategorySection-cvaRoot', 'mt-6.7']);

const cvaTotalCount = cva([
  'CatalogCategorySection-cvaTotalCount',
  'px-0.6 py-0.4 rounded-[0.8rem]',
  'text-bodyM text-cBlack64',
  'bg-cLightGrey',
]);

export const cvaProductsList = cva(
  [
    'CatalogCategorySection-cvaProductsList',
    'relative',
    'grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-0.8 sm:gap-1.2',
    '-mx-1.6 sm:mx-0 pt-1.6',
    'before:absolute before:left-0 before:top-0 before:w-full before:h-full before:bg-cWhite before:opacity-0 before:[visibility:hidden] before:transition-all before:duration-300 before:z-[2]',
  ],
  {
    variants: {
      isLoading: {
        true: ['before:opacity-100 bg-cWhite/60 before:[visibility:visible]'],
        false: [''],
      },
    },
  }
);

export const cvaCatalogFooter = cva(['CatalogCategorySection-cvaCatalogFooter', 'pt-4.8']);

// const cvaShowMore = cva([
//   'CatalogCategorySection-cvaShowMore',
//   'mb-3.2',
//   'text-center',
// ]);

// const cvaShowMoreButton = cva([
//   'CatalogCategorySection-cvaShowMoreButton',
//   'inline-flex items-center justify-center gap-[0.45rem]',
//   'min-w-[24.7rem] sm:min-w-[34rem]',
//   'py-[1.8rem] sm:py-[2.45rem] pb-[2.2rem] sm:pb-[2.85rem] px-2',
//   'text-center text-bodyLSemibold',
//   'rounded-[3.2rem] border border-0.1 border-cBlack',
// ]);

export const cvaShowMoreButtonIcon = cva([
  'CatalogCategorySection-cvaShowMoreButtonIcon',
  'w-1.8 h-1.8',
  'relative top-0.2',
  'stroke-cBlack',
]);
